/**
 * Bottom-docked transport UI. Session state is synced from the server
 * (see usePlaybackSync); controls call POST /playback/*.
 */
import { useEffect, useMemo } from "react";
import {
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from "lucide-react";
import { applyVoiceOutputMode, isOwnerActive, usePlayer } from "../state/player";
import { fmtDuration } from "../lib/format";
import { CoverImg } from "./CoverImg";
import { LoudnessIndicator } from "./LoudnessIndicator";
import { OwnerBadge } from "./OwnerBadge";
import { useReportTime, useVoiceStatus } from "../hooks/useStats";
import { tickPlaybackProgress } from "../lib/playbackSync";

export const Player: React.FC = () => {
  const queue = usePlayer((s) => s.queue);
  const index = usePlayer((s) => s.index);
  const isPlaying = usePlayer((s) => s.isPlaying);
  const currentTime = usePlayer((s) => s.currentTime);
  const duration = usePlayer((s) => s.duration);
  const volume = usePlayer((s) => s.volume);
  const muted = usePlayer((s) => s.muted);
  const activeOwners = usePlayer((s) => s.activeOwners);
  const play = usePlayer((s) => s.play);
  const pause = usePlayer((s) => s.pause);
  const next = usePlayer((s) => s.next);
  const prev = usePlayer((s) => s.prev);
  const seek = usePlayer((s) => s.seek);
  const setVolume = usePlayer((s) => s.setVolume);
  const setMuted = usePlayer((s) => s.setMuted);
  const setOwnerActive = usePlayer((s) => s.setOwnerActive);

  const track = queue[index];
  const voiceStatus = useVoiceStatus();
  const voiceConnected = usePlayer((s) => s.voiceConnected);
  const reportTime = useReportTime();

  const ownersInQueue = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of queue) {
      counts.set(t.owner, (counts.get(t.owner) ?? 0) + 1);
    }
    return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [queue]);

  useEffect(() => {
    if (!isPlaying) return;
    const handle = setInterval(() => {
      const s = usePlayer.getState();
      void reportTime.mutateAsync(s.currentTime);
    }, 5_000);
    return () => clearInterval(handle);
  }, [isPlaying, reportTime]);

  // Absolute timeline progress — independent of <audio> and Discord metrics.
  useEffect(() => {
    let raf = 0;
    const loop = (): void => {
      tickPlaybackProgress();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const el = usePlayer.getState().audio;
    if (!el) return;
    applyVoiceOutputMode(el, voiceConnected, volume, muted);
  }, [voiceConnected, volume, muted]);

  if (!track) {
    return (
      <div className="flex items-center justify-between gap-4 text-sm text-gray-500 dark:text-gray-400">
        <span>Nothing queued. Build a shuffle from the Library page.</span>
      </div>
    );
  }

  const effectiveDuration = duration ?? track.durationSec ?? null;
  const progress =
    effectiveDuration && effectiveDuration > 0
      ? (currentTime / effectiveDuration) * 100
      : 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <CoverImg track={track} className="h-12 w-12" rounded="rounded-lg" />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-sm font-medium">
              {track.metadataTitle ?? track.name}
            </div>
            <OwnerBadge owner={track.owner} className="shrink-0" />
            <LoudnessIndicator track={track} className="shrink-0" />
          </div>
          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
            {track.metadataArtist ?? "Unknown artist"} — {index + 1}/{queue.length}
            {voiceStatus.data?.connected ? (
              <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800 dark:bg-green-950 dark:text-green-200">
                Playing in voice — browser muted
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
            <span>{fmtDuration(currentTime)}</span>
            <div
              className="relative h-1 flex-1 cursor-pointer rounded-full bg-gray-200 dark:bg-gray-700"
              onClick={(e) => {
                if (!effectiveDuration) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - rect.left) / rect.width;
                seek(pct * effectiveDuration);
              }}
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-indigo-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span>{fmtDuration(effectiveDuration)}</span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            <button className="btn" onClick={prev} aria-label="Previous">
              <SkipBack className="h-4 w-4" />
            </button>
            <button
              className="btn btn-primary"
              onClick={() => (isPlaying ? pause() : play())}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
            <button className="btn" onClick={next} aria-label="Next">
              <SkipForward className="h-4 w-4" />
            </button>
            <button
              className="btn"
              onClick={() => setMuted(!muted)}
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="hidden w-24 accent-indigo-600 md:inline-block"
              aria-label="Volume"
              title={voiceConnected ? "Discord voice volume" : "Browser volume"}
            />
          </div>
          {voiceConnected ? (
            <span className="hidden text-[10px] text-gray-500 md:inline">
              Volume controls Discord output
            </span>
          ) : null}
        </div>
      </div>

      {ownersInQueue.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800/50">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Active in shuffle
          </span>
          {ownersInQueue.map(([owner, count]) => {
            const active = isOwnerActive(activeOwners, owner);
            return (
              <label
                key={owner}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-0.5 text-xs transition ${
                  active
                    ? "bg-white shadow-sm dark:bg-gray-700"
                    : "opacity-60"
                }`}
                title={`${active ? "Exclude" : "Include"} ${owner} (${count} tracks)`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setOwnerActive(owner, e.target.checked)}
                  className="h-3 w-3 accent-indigo-600"
                />
                <OwnerBadge owner={owner} />
                <span className="text-gray-500">({count})</span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
